import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { hidePartOfEmail } from '../../utils/str';
import { checkCodeSignIsValid, requestCodeSign, resendCodeSignEmail, sendCodeSignToValidate, verifyTokenBeforeSign } from '../../services/assinatura';
import { MdCheckCircle } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { onTermFlowAccept } from '../../services/form-flow';
import Terms from '../Terms';
import TokenFeedback from '../TokenFeedback';
import getCookie from '../../utils/getCookies';

type TermsProps = {
  titulo: string;
  conteudo: string;
  versao: string;
  id: string;
}

interface Props {
  page: number;
  handleStep: (pageOrStep: number) => void;
  handleSubmit: (values: any, errors?: any) => void;
  hasTerms?: TermsProps | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#264259',
      },
    },
  },
  title: {
    fontFamily: 'Roboto',
    fontWeight: 500,
    color: '#264259',
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: 16,
    padding: 20,
  },
  subtitle: {
    opacity: 0.6,
    textAlign: 'center',
    fontWeight: 'normal',
    fontSize: 16,
  },

  input: {
    color: theme.palette.primary.main,
  },
  buttonForm: {
    backgroundColor: '#264259',
  },
  textField: {
  },
}));

export default function SignForm({
  page,
  handleStep,
  handleSubmit,
  hasTerms
}: Props): JSX.Element {
  const classes = useStyles();
  const history = useHistory();
  const [valueCode, setValueCode] = useState<string>('');
  const [userData, setUserData] = useState<any>({});
  const [dataForm, setDataForm] = useState<any>({});
  const [, setHashSign] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [stateCode, setStateCode] = useState<string>('');
  const [endSign, setEndSign] = useState<boolean>(false);
  const [loadingTerms, setLoadingTerms] = useState<boolean>(false);
  const [stepLocal, setStepLocal] = useState<number>(0);
  const [isEmailResent, setIsEmailResent] = useState<boolean>(false);
  const [tokenFlowExecuted, setTokenFlowExecuted] = useState<boolean>(false);
  const hasExecutedRef = useRef<boolean>(false);
  const isValidatingRef = useRef<boolean>(false);
  const [tokenStatus, setTokenStatus] = useState<{
    isValid: boolean;
    hasToken: boolean;
    expirationTime?: string;
    timeRemaining?: string;
  }>({
    isValid: false,
    hasToken: false,
  });

  useEffect(() => {
    setUserData(JSON.parse(getCookie('gov_user_data') || '{}'));
    setDataForm(JSON.parse(getCookie('gov_user_form_started') || '{}'));

  }, []);

  const checkExistingToken = async () => {
    return false;
  };

  const verifyExistingToken = async (): Promise<{ isValid: boolean; expirationTime?: string }> => {
    try {      
      const cpf = userData?.cpf;
      const email = userData?.contato?.email;
      
      if (!cpf || !email) {
        return { isValid: false };
      }
      
      const response = await verifyTokenBeforeSign(cpf, email);
      const data = response?.data;
      
      if (data && (data.valid === true || data.has_valid_token === true)) {
        const timeRemaining = data.time_remaining || data.timeRemaining;
        
        let expirationTime = undefined;
        const expiresField = data.expires || data.expired_at || data.expiration_time || data.expires_at;
        
        if (expiresField) {
          try {
            const expDate = new Date(expiresField);
            if (!isNaN(expDate.getTime())) {
              expirationTime = `${expDate.getHours().toString().padStart(2, '0')}:${expDate.getMinutes().toString().padStart(2, '0')}`;
            }
          } catch (e) {
            expirationTime = undefined;
          }
        }
        
        setTokenStatus({
          isValid: true,
          hasToken: true,
          timeRemaining: timeRemaining,
          expirationTime: expirationTime
        });
        
        if (data.state || data.code) {
          setStateCode(data.state || data.code);
        }
        
        return { isValid: true, expirationTime };
      } else if (data) {

        let expirationTime = undefined;
        const expiresField = data?.expires || data?.expired_at || data?.expiration_time || data?.expires_at;

        if (expiresField) {
          try {
            const expDate = new Date(expiresField);
            if (!isNaN(expDate.getTime())) {
              expirationTime = `${expDate.getHours().toString().padStart(2, '0')}:${expDate.getMinutes().toString().padStart(2, '0')}`;
            } else {
              expirationTime = 'Expiração inválida';
            }
          } catch (e) {
            expirationTime = 'Expiração inválida';
          }
        } else {
          expirationTime = 'Expiração não informada';
        }

        setTokenStatus({
          isValid: false,
          hasToken: true,
          expirationTime: expirationTime
        });
        
        if (data.state || data.code) {
          setStateCode(data.state || data.code);
        }

        return { isValid: false, expirationTime };
      } else {
        return { isValid: false };
      }
    } catch (error) {
      return { isValid: false };
    }
  };

  const getTokenDetails = async (hashToCheck: string) => {
    try {
      if (dataForm?.identificador && hashToCheck) {
        const { data } = await checkCodeSignIsValid(dataForm.identificador, hashToCheck);
        
        if (data && (data.valid || data.validado)) {
          setTokenStatus(prevStatus => ({
            ...prevStatus,
            timeRemaining: data.time_remaining || data.timeRemaining
          }));
        }
      }
    } catch (error) {
      console.log('Erro ao obter detalhes do token:', error);
    }
  };

  const requestCode = async (isNewRequest = false, existingExpirationTime?: string) => {
    try {
      const { data } = await requestCodeSign(userData?.cpf, userData?.contato?.email);
      setStateCode(data.state);
      
      const timeRemaining = data.time_remaining || data.timeRemaining;
      const isNewCode = data.is_new_request || data.isNewRequest || data.new_code;
      
      if (isNewCode || data.message?.includes('enviado') || data.message?.includes('novo')) {
        const expiredAt = data.expired_at || data.expiration_time || data.previous_expired_at || data.expires || data.previous_expires;

        let formattedExpirationTime = '';
        
        if (expiredAt) {
          try {
            const expDate = new Date(expiredAt);
            if (!isNaN(expDate.getTime())) {
              formattedExpirationTime = `${expDate.getHours().toString().padStart(2, '0')}:${expDate.getMinutes().toString().padStart(2, '0')}`;
            } else if (existingExpirationTime) {
              formattedExpirationTime = existingExpirationTime;
            } else {
              formattedExpirationTime = 'Expiração não informada';
            }
          } catch (parseError) {
            console.error('Erro ao processar horário de expiração:', parseError);

            if (existingExpirationTime) {
              formattedExpirationTime = existingExpirationTime;
            } else {
              formattedExpirationTime = 'Expiração não informada';
            }
          }
        } else if (existingExpirationTime) {
          formattedExpirationTime = existingExpirationTime;
        } else {
          formattedExpirationTime = 'Expiração não informada';
        }
        
        setTokenStatus({
          isValid: false,
          hasToken: true,
          expirationTime: formattedExpirationTime
        });
      } else {
        setTokenStatus({
          isValid: true,
          hasToken: true,
          timeRemaining: timeRemaining || undefined 
        });
      }
      
    } catch (error) {
      setTokenStatus({
        isValid: false,
        hasToken: true,
        expirationTime: 'Erro ao obter expiração'
      });
    }
  };

  const resendEmail = async () => {
    try {      
      if (userData?.cpf && userData?.contato?.email) {
        const { data } = await resendCodeSignEmail(userData.cpf, userData.contato.email);        
        const expiredAt = data.expired_at || data.expiration_time || data.expires || data.expires_at;
        const timeRemaining = data.time_remaining || data.timeRemaining;
        
        let formattedExpirationTime = '';
        
        if (expiredAt) {
          try {
            const expDate = new Date(expiredAt);
            if (!isNaN(expDate.getTime())) {
              formattedExpirationTime = `${expDate.getHours().toString().padStart(2, '0')}:${expDate.getMinutes().toString().padStart(2, '0')}`;
            } else {
              formattedExpirationTime = 'Expiração não informada';
            }
          } catch (parseError) {
            formattedExpirationTime = 'Expiração não informada';
          }
        } else {
          formattedExpirationTime = 'Expiração não informada';
        }
        
        setTokenStatus({
          isValid: true,
          hasToken: true,
          timeRemaining: timeRemaining,
          expirationTime: formattedExpirationTime
        });
        
        setIsEmailResent(true);
      } else {
        alert('Dados de usuário incompletos. Por favor, tente novamente.');
      }
    } catch (error: any) {
      alert('Erro ao reenviar e-mail. Tente novamente.');
    }
  }
  
  const handleTokenFlow = async () => {
    if (hasExecutedRef.current) {
      return;
    }

    hasExecutedRef.current = true;

    try {
      setTokenFlowExecuted(true);
      
      const tokenInfo = await verifyExistingToken();
      
      if (tokenInfo.isValid) {
        return;
      }
      
      await requestCode(true, tokenInfo.expirationTime);
      
    } catch (error) {
      return;
    }
  };

  useEffect(() => {
    if (userData?.cpf && userData?.contato?.email && dataForm?.identificador && !hasExecutedRef.current) {
      handleTokenFlow();
    }
  }, [userData, dataForm]);

  const sendCodetoValidate = async () => {
    if (loading || isValidatingRef.current) return;
    if (!valueCode || !dataForm) return;
    isValidatingRef.current = true;
    setLoading(true);
    let shouldResetLoading = true;
    try {
      const clientId = process.env.REACT_APP_FORMS_CLIENT_ID;
      let decodedClientId = '';
      try {
        decodedClientId = atob(clientId || '');
      } catch (decodeError) {
        console.error('Erro ao decodificar client_id:', decodeError);
      }
      const payload = {
        code: valueCode,
        state: stateCode,
        identificador: dataForm.identificador,
        tipo: 'Solicitacao',
        internal_state: stateCode,
        client_id: decodedClientId,
      };
      const { data } = await sendCodeSignToValidate(payload);
      if (data?.hash) {
        setHashSign(data.hash);
        try {
          await checkSign(data.hash);
        } catch (checkError) {
          console.error('Erro ao checar assinatura após validação:', checkError);
        }
        try {
          handleSubmit({
            hash: data.hash,
            identificador: dataForm.identificador,
          });
        } catch (submitError) {
          console.error('Erro ao processar sucesso da assinatura:', submitError);
        }
        setEndSign(true);
        history.push('/workspace/minhas_solicitacoes');
        shouldResetLoading = false;
        return;
      }
      alert('Não foi possível validar o código. Tente novamente ou use "Reenviar e-mail".');
    } catch (error) {
      if (error?.response?.status === 404) {
        alert('Código inválido. Por favor, busque o código válido em seu email cadastrado.');
      } else {
        alert(error?.response?.data?.message || 'Erro ao validar código');
      }
    } finally {
      if (shouldResetLoading) {
        isValidatingRef.current = false;
        setLoading(false);
      }
    }
  }

  const checkSign = async (hash: string): Promise<boolean> => {
    try {
      if (dataForm) {
        const { data } = await checkCodeSignIsValid(dataForm.identificador, hash);
        if (data && (data.valid || data.validado)) {
          setTokenStatus({
            isValid: true,
            hasToken: true,
            timeRemaining: data.time_remaining || data.timeRemaining
          });
          return true;
        }
        return false;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleSubmitTerms = async () => {
    const dataForm = JSON.parse(getCookie('gov_user_form_started') || '{}')

    if (!hasTerms && !dataForm) return;
    try {
      setLoadingTerms(true);
      await onTermFlowAccept(dataForm.id, hasTerms.id);
      setLoadingTerms(false);
      setStepLocal(1);
    } catch (error) {
      setLoadingTerms(false);
    }
  }

  const handleBackPage = () => {
    if (page > 0) {
      handleStep(page - 1);
    }
  };

  useEffect(() => {
    if (!hasTerms) {
      setStepLocal(1);
    }
  }, [hasTerms]);

  return (
    <Box>
      {stepLocal === 0 && hasTerms && (
        <Terms
          titulo={hasTerms.titulo}
          conteudo={hasTerms.conteudo}
          onSubmit={handleSubmitTerms}
          versao={hasTerms.versao}
          loading={loadingTerms}
        />
      )}

      {stepLocal === 1 && (
        <>
          {!endSign ? <>
            <Grid
              container
              direction="column"
              alignItems="center"
              justify="center"
            >
              <Grid>
                <Typography variant="h6" className={classes.title}>
                  Assinatura
                </Typography>
              </Grid>
              
              {tokenStatus.hasToken && (
                <>
                  <TokenFeedback 
                    isTokenValid={tokenStatus.isValid}
                    userEmail={userData?.contato?.email || ''}
                    timeRemaining={tokenStatus.timeRemaining}
                    tokenExpirationTime={tokenStatus.expirationTime}
                    isResent={isEmailResent}
                  />
                </>
              )}
              
              {!tokenStatus.hasToken && (
                <Grid style={{ textAlign: 'center', marginBottom: 20 }}>
                  <Typography className={classes.subtitle}>
                    Assine a solicitação com o código enviado para seu email: {hidePartOfEmail(userData?.contato?.email || '')}
                  </Typography>
                </Grid>
              )}

              <TextField
                id='codeValidate'
                fullWidth
                label="Digite o código de 6 dígitos"
                variant="outlined"
                className={classes.textField}
                inputProps={{
                  className: classes.input,
                }}
                onChange={(e) => setValueCode(e.target.value)}
                disabled={loading}
              />
              {loading ? <CircularProgress /> : (<>
                <Button
                  className={classes.buttonForm}
                  variant="contained"
                  fullWidth
                  color="primary"
                  type="submit"
                  style={{ marginTop: 20 }}
                  onClick={sendCodetoValidate}
                  disabled={loading || !stateCode}
                >
                  Assinar
                </Button>
                <Button
                  style={{ color: '#0F6FB7' }}
                  variant='text'
                  onClick={() => resendEmail()}
                  disabled={loading}
                >
                  Reenviar e-mail
                </Button>
                {page > 0 && (
                  <Button
                    style={{ color: '#264259', marginTop: 20 }}
                    variant="text"
                    onClick={handleBackPage}
                    disabled={loading}
                  >
                    VOLTAR
                  </Button>
                )}
              </>)}

            </Grid>
          </>
            :
            <>
              <Grid
                container
                direction="column"
                alignItems="center"
                justify="center"
              >

                <Grid>
                  <MdCheckCircle size={30} color="#0BB07B" />
                </Grid>
                <Grid>
                  <Typography variant="h6" className={classes.title}>
                    Solicitação realizada com sucesso!
                  </Typography>
                </Grid>



                {loading ? <CircularProgress /> : (<>
                  <Button
                    className={classes.buttonForm}
                    variant="contained"
                    fullWidth
                    color="primary"
                    type="submit"
                    style={{ marginTop: 20 }}
                    onClick={() => history.push('/workspace/minhas_solicitacoes')}
                  >
                    Minhas solicitações
                  </Button>
                  <Button style={{ color: '#0F6FB7' }} variant='text' onClick={() => history.push('/')} >Home </Button>
                </>)}

              </Grid>
            </>}
        </>
      )}
    </Box>
  );
}
